import { BaseComponent } from "utils/base-component"
import { MobileUnitSwitcher } from "./utils/mobile-unit-switcher"
import { MobileMenu } from "./utils/mobile-menu"
import { MobileDropdowns } from "./utils/mobile-dropdowns"
import { DesktopDropdowns } from "./utils/desktop-dropdowns"
import "./page-header.scss"
import "./components/account-favourites-dropdown.scss"
import "./components/forecast-channels.scss"
import "./components/my-mountains.scss"
import "./components/settings-nav.scss"
import "./components/mobile/menu-nav.scss"

require.context("./static", true, /\.svg|png$/)

export class PageHeader extends BaseComponent {
  static cssClass = "page-header"

  private mobileUnitSwitcher!: MobileUnitSwitcher
  private mobileMenu!: MobileMenu
  private mobileDropdowns!: MobileDropdowns
  private desktopDropdowns!: DesktopDropdowns
  private documentClickHandler!: (event: MouseEvent) => void

  init() {
    // call components that need to be initialized
    this.mobileUnitSwitcher = new MobileUnitSwitcher(this.cssClass)
    this.mobileMenu = new MobileMenu(this.cssClass)
    this.mobileDropdowns = new MobileDropdowns(this.cssClass)
    this.desktopDropdowns = new DesktopDropdowns(this.cssClass)

    // Only add document click handler if mobile components are available
    if (this.mobileMenu && this.mobileDropdowns) {
      this.addDocumentClickHandler()
    } else {
      // eslint-disable-next-line no-console
      console.warn("Mobile menu components not available, skipping document click handler")
    }

    // Debug: Log component status
    // console.log('MobileMenu component:', this.mobileMenu)
    // console.log('MobileDropdowns component:', this.mobileDropdowns)

    // Handle hover for desktop nav items and dropdowns
    document.querySelectorAll(".page-header__nav-item").forEach((navItem) => {
      const button = navItem.querySelector("button")
      const dropdownId = button?.getAttribute("aria-controls")
      const dropdown = dropdownId ? document.getElementById(dropdownId) : null

      if (!dropdown || !button) return

      // Make dropdown items focusable
      const dropdownItems = dropdown.querySelectorAll("a, button")
      dropdownItems.forEach((item) => {
        if (!item.hasAttribute("tabindex")) {
          item.setAttribute("tabindex", "0")
        }
      })

      // Track expanded state
      let isExpanded = false

      const expandDropdown = () => {
        dropdown.classList.add("active")
        button.setAttribute("aria-expanded", "true")
        dropdown.setAttribute("aria-hidden", "false")
        isExpanded = true
      }

      const collapseDropdown = () => {
        dropdown.classList.remove("active")
        button.setAttribute("aria-expanded", "false")
        dropdown.setAttribute("aria-hidden", "true")
        isExpanded = false
      }

      // Show dropdown on nav-item hover (desktop only)
      navItem.addEventListener("mouseenter", () => {
        // Only apply hover behavior on desktop screens
        if (window.innerWidth >= 948) {
          expandDropdown()
        }
      })

      // Hide dropdown when leaving nav-item (desktop only)
      navItem.addEventListener("mouseleave", () => {
        // Only apply hover behavior on desktop screens
        if (window.innerWidth >= 948) {
          // Don't collapse if favourites dropdown is open and not already expanded
          if (this.desktopDropdowns && this.desktopDropdowns.hasOpenFavouritesDropdown()) {
            return
          }
          collapseDropdown()
        }
      })

      // Show dropdown on button focus (for keyboard/screen reader users - desktop only)
      button.addEventListener("focus", () => {
        // Only apply focus behavior on desktop screens
        if (window.innerWidth >= 948) {
          expandDropdown()
        }
      })

      // Keep dropdown visible when hovering over it (desktop only)
      dropdown.addEventListener("mouseenter", () => {
        // Only apply hover behavior on desktop screens
        if (window.innerWidth >= 948) {
          expandDropdown()
        }
      })

      // Keep dropdown visible when interacting with favourites dropdown
      dropdown.addEventListener("click", (e) => {
        // If clicking on favourites button or favourites dropdown, keep account dropdown open
        if (
          (e.target && (e.target as Element).closest("#favourites")) ||
          (e.target && (e.target as Element).closest(".account-favourites-dropdown"))
        ) {
          expandDropdown()
        }
      })

      // Keep dropdown visible when focusing on dropdown items (desktop only)
      dropdown.addEventListener("focusin", () => {
        // Only apply focus behavior on desktop screens
        if (window.innerWidth >= 948) {
          expandDropdown()
        }
      })

      // Handle keyboard navigation (desktop only)
      button.addEventListener("keydown", (event) => {
        // Only apply keyboard behavior on desktop screens
        if (window.innerWidth >= 948 && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault()
          if (!isExpanded) {
            expandDropdown()
          } else {
            collapseDropdown()
            button.focus()
          }
        }
      })

      // Hide dropdown when focus leaves entirely (desktop only)
      dropdown.addEventListener("focusout", () => {
        // Only apply focus behavior on desktop screens
        if (window.innerWidth >= 948) {
          setTimeout(() => {
            const { activeElement } = document
            if (!dropdown.contains(activeElement) && !button.contains(activeElement)) {
              collapseDropdown()
            }
          }, 200)
        }
      })
    })

    // Handle window resize to update hover behavior
    window.addEventListener("resize", () => {
      // If switching to mobile, close any open desktop dropdowns
      if (window.innerWidth < 948) {
        document.querySelectorAll(".page-header__nav-item").forEach((navItem) => {
          const button = navItem.querySelector("button")
          const dropdownId = button?.getAttribute("aria-controls")
          const dropdown = dropdownId ? document.getElementById(dropdownId) : null

          if (dropdown && button) {
            dropdown.classList.remove("active")
            button.setAttribute("aria-expanded", "false")
            dropdown.setAttribute("aria-hidden", "true")
          }
        })
      }
    })
  }

  /**
   * Adds a document click event listener to close mobile menus when clicking outside
   */
  private addDocumentClickHandler(): void {
    // Store references to avoid 'this' context issues in event listeners
    const mobileMenuRef = this.mobileMenu
    const mobileDropdownsRef = this.mobileDropdowns

    this.documentClickHandler = (event: MouseEvent) => {
      const target = event.target as Node

      // debug to ensure components are initialized
      // if (!mobileMenuRef || !mobileDropdownsRef) {
      //   console.warn('Mobile menu components not initialized')
      //   console.log('mobileMenu:', mobileMenuRef)
      //   console.log('mobileDropdowns:', mobileDropdownsRef)
      //   return
      // }

      // debug to ensure methods exist
      // if (
      //   !mobileMenuRef.isMenuClick ||
      //   !mobileMenuRef.isMenuButtonClick ||
      //   !mobileMenuRef.closeAllMobileMenus ||
      //   !mobileDropdownsRef.isDropdownClick ||
      //   !mobileDropdownsRef.isDropdownButtonClick ||
      //   !mobileDropdownsRef.closeAllDropdowns
      // ) {
      //   console.warn('methods not available')
      //   console.log('mobileMenu methods:', {
      //     isMenuClick: mobileMenuRef.isMenuClick,
      //     isMenuButtonClick: mobileMenuRef.isMenuButtonClick,
      //     closeAllMobileMenus: mobileMenuRef.closeAllMobileMenus
      //   })
      //   console.log('mobileDropdowns methods:', {
      //     isDropdownClick: mobileDropdownsRef.isDropdownClick,
      //     isDropdownButtonClick: mobileDropdownsRef.isDropdownButtonClick,
      //     closeAllDropdowns: mobileDropdownsRef.closeAllDropdowns
      //   })
      //   return
      // }

      // Check if click is inside any mobile menu or on menu buttons
      const isMenuClick =
        mobileMenuRef.isMenuClick(target) || mobileMenuRef.isMenuButtonClick(target)
      const isDropdownClick =
        mobileDropdownsRef.isDropdownClick(target) ||
        mobileDropdownsRef.isDropdownButtonClick(target)

      // Check if click is inside desktop favourites dropdown or on favourites button
      const isDesktopDropdownClick =
        this.desktopDropdowns.isFavouritesDropdownClick(target) ||
        this.desktopDropdowns.isFavouritesButtonClick(target)

      // If click is outside all menus and dropdowns, close them
      if (!isMenuClick && !isDropdownClick && !isDesktopDropdownClick) {
        // Close all mobile menus
        mobileMenuRef.closeAllMobileMenus()
        // Close all mobile dropdowns
        mobileDropdownsRef.closeAllDropdowns()
        // Close desktop favourites dropdown
        this.desktopDropdowns.closeFavouritesDropdown()
      }
    }

    // Add the event listener
    document.addEventListener("click", this.documentClickHandler)
  }

  /**
   * Cleanup method to remove event listeners
   */
  destroy(): void {
    if (this.documentClickHandler) {
      document.removeEventListener("click", this.documentClickHandler)
    }
  }
}
